import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import classnames from "classnames";
import Fuse from "fuse.js";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { BaseRedoInput } from "./arbiter-components/input/base-redo-text-input";
import * as styles from "./column-selector.module.css";
import { Dropdown } from "./dropdown";
import { Flex } from "./flex";
import { Column2 } from "./table2";
import { Text } from "./text";

interface ColumnItemProps<T> {
  item: Column2<T>;
  commonProps: { onSelect: (selected: boolean, key: string) => void };
  dragHandleProps: any;
  showDragHandle?: boolean;
}

export const ColumnSelector = genericMemo(function ColumnSelection<T>({
  columns,
  dropdownButtonRef,
  displayedColumns,
  setDisplayedColumns,
}: {
  columns: Column2<T>[];
  dropdownButtonRef: HTMLButtonElement | null;
  displayedColumns: { key: string; displayed: boolean }[];
  setDisplayedColumns: (value: { key: string; displayed: boolean }[]) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  class ColumnItem extends React.Component<
    ColumnItemProps<T>,
    { hovered: boolean }
  > {
    constructor(props: ColumnItemProps<T>) {
      super(props);
      this.state = { hovered: false };
    }

    handleSelect = (selected: boolean) => {
      const { item, commonProps } = this.props;
      commonProps.onSelect(selected, item.key);
    };

    handleHover = (hovered: boolean) => {
      this.setState({ hovered });
    };

    override render() {
      const { item } = this.props;
      const { hovered } = this.state;

      const showDragHandle = this.props.showDragHandle ?? true;
      return (
        <Flex
          align="center"
          className={classnames(
            styles.columnItem,
            showDragHandle && hovered ? styles.columnItemDraggable : "",
          )}
          dir="row"
          onMouseEnter={() => this.handleHover(true)}
          onMouseLeave={() => this.handleHover(false)}
          {...(showDragHandle ? this.props.dragHandleProps : {})}
        >
          <Flex dir="row" gap="xl" grow={1}>
            <div onClick={(e) => e.stopPropagation()}>
              <RedoCheckbox
                dataTestId={`column-selector-checkbox-${item.key}`}
                setValue={this.handleSelect}
                size="xs"
                value={
                  displayedColumns.find((col) => col.key === item.key)
                    ?.displayed ?? false
                }
              />
            </div>
            <Text fontSize="xs">{item.title}</Text>
          </Flex>
          {showDragHandle && (
            <div
              className={classnames(
                styles.dragHandle,
                hovered ? styles.dragHandleHovered : "",
              )}
            >
              <DotsReorderIcon className={styles.icon} />
            </div>
          )}
        </Flex>
      );
    }
  }

  const correctlySortedCols = useMemo(() => {
    const displayOrderMap = new Map(
      displayedColumns.map((col, index) => [col.key, index]),
    );
    return columns
      .sort((a, b) => {
        const orderA = displayOrderMap.get(a.key) ?? Infinity;
        const orderB = displayOrderMap.get(b.key) ?? Infinity;
        return orderA - orderB;
      })
      .filter((col) => !col.nonHidable);
  }, [columns, displayedColumns]);

  const searcher = useMemo(
    () => new Fuse(correctlySortedCols, { keys: ["title"], threshold: 0.2 }),
    [correctlySortedCols],
  );

  const filteredColumns = useSearch(searcher, correctlySortedCols, searchText);

  const toggleDropdownOpen = useHandler(() => {
    setDropdownOpen((prev) => {
      const newOpen = !prev;
      return newOpen;
    });
    setSearchText("");
  });

  useEffect(() => {
    if (!dropdownButtonRef) {
      return;
    }
    dropdownButtonRef.addEventListener("click", toggleDropdownOpen);
    return () => {
      dropdownButtonRef.removeEventListener("click", toggleDropdownOpen);
    };
  }, [dropdownButtonRef, toggleDropdownOpen]);

  return (
    <>
      {dropdownOpen && (
        <ClickAwayListener onClickAway={toggleDropdownOpen}>
          <Dropdown
            anchor={dropdownButtonRef}
            className={styles.dropdown}
            fitToAnchor={false}
            flexProps={{ p: "none", gap: "none" }}
            open
          >
            <Flex dir="column" gap="none">
              <Flex
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                className={styles.filterDropdownHeader}
                px="lg"
                py="md"
              >
                <BaseRedoInput
                  autoFocus
                  placeholder="Search columns..."
                  setValue={setSearchText}
                  size="sm"
                  value={searchText}
                />
              </Flex>
              <Flex dir="column" px="lg" py="md" ref={dropdownContainerRef}>
                {searchText.trim().length > 0 ? (
                  // It doesn't make sense to reorder columns when filtered
                  filteredColumns.map((col) => (
                    <ColumnItem
                      commonProps={{
                        onSelect: (selected: boolean, key: string) => {
                          const columnIndex = displayedColumns.findIndex(
                            (c) => c.key === key,
                          );
                          if (columnIndex !== -1) {
                            const updatedColumns = [...displayedColumns];
                            updatedColumns[columnIndex].displayed = selected;
                            setDisplayedColumns(updatedColumns);
                          }
                        },
                      }}
                      dragHandleProps={{}}
                      item={col}
                      key={col.key}
                      showDragHandle={false}
                    />
                  ))
                ) : (
                  <DraggableList<
                    Column2<T>,
                    { onSelect: (selected: boolean, key: string) => void },
                    ColumnItem
                  >
                    commonProps={{
                      onSelect: (selected: boolean, key: string) => {
                        const columnIndex = displayedColumns.findIndex(
                          (col) => col.key === key,
                        );
                        if (columnIndex !== -1) {
                          const updatedColumns = [...displayedColumns];

                          if (selected) {
                            updatedColumns[columnIndex].displayed = true;
                          } else {
                            updatedColumns[columnIndex].displayed = false;
                          }

                          setDisplayedColumns(updatedColumns);
                        }
                      },
                    }}
                    constrainDrag
                    container={() => dropdownContainerRef.current}
                    itemKey={(item: Column2<T>) => item.key}
                    list={correctlySortedCols}
                    onMoveEnd={(newList) => {
                      const newDisplayedColumns: {
                        key: string;
                        displayed: boolean;
                      }[] = [];
                      for (const item of newList) {
                        newDisplayedColumns.push({
                          key: item.key,
                          displayed:
                            displayedColumns.find((col) => col.key === item.key)
                              ?.displayed ?? false,
                        });
                      }

                      for (const col of columns.reverse()) {
                        if (col.nonHidable) {
                          newDisplayedColumns.unshift({
                            key: col.key,
                            displayed: true,
                          });
                        }
                      }

                      setDisplayedColumns([...newDisplayedColumns]);
                    }}
                    padding={8}
                    template={ColumnItem}
                  />
                )}
              </Flex>
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
});
